import { useCallback, useMemo } from 'react'
import { object, string } from 'yup'

import type { EInvoiceLifecycleAction } from '@features/eInvoicing/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useForm } from '../../../hooks'
import { type LifecycleActionPayload, useLifecycleAction } from './useLifecycleAction'

interface LifecycleActionForm {
  reasonCode?: string
  reason?: string
}

const DEFAULT_VALUES: LifecycleActionForm = { reasonCode: '', reason: '' }

interface UseLifecycleActionFormProps {
  action: EInvoiceLifecycleAction
  attachmentId: string
  onSuccess: (payload: LifecycleActionPayload) => void
  reasonCodeRequiredMessage: string
  mandatoryReasonCodes: Set<string>
  commentRequiredMessage: string
}

export const useLifecycleActionForm = ({
  action,
  attachmentId,
  onSuccess,
  reasonCodeRequiredMessage,
  mandatoryReasonCodes,
  commentRequiredMessage,
}: UseLifecycleActionFormProps) => {
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()
  const { submit, isProcessing } = useLifecycleAction({ action, onSuccess })
  const { Form, FormItem, handleSubmit, control, clearErrors } = useForm({
    defaultValues: DEFAULT_VALUES,
    validationSchema: useMemo(
      () =>
        object({
          reasonCode: string().required(reasonCodeRequiredMessage),
          reason: string().when('reasonCode', {
            is: (code: string) => mandatoryReasonCodes.has(code),
            then: (schema) => schema.required(commentRequiredMessage),
          }),
        }),
      [reasonCodeRequiredMessage, mandatoryReasonCodes, commentRequiredMessage],
    ),
  })

  const handleSubmitForm = useCallback(
    ({ reason, reasonCode }: LifecycleActionForm) => {
      submit({ organizationId: organization?.id, attachmentId, reason, reasonCode })
    },
    [submit, organization?.id, attachmentId],
  )

  return {
    Form,
    FormItem,
    submitForm: handleSubmit(handleSubmitForm),
    control,
    clearErrors,
    isProcessing,
    isOrganizationLoading,
  }
}
